import { lazy, type ComponentType } from 'react'
import type { AnyRouter } from '@tanstack/react-router'

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** The detail panes a page above this route renders for it; a deep link loads them before hydrating. */
    detailPanes?: { preload: () => Promise<unknown> }
  }
}

/**
 * A section's DETAIL panes as one on-demand module: the list page renders them only once an item is selected, so
 * they stay off its critical path. `preload` is what the item's `$id` route loader calls, which the router runs on
 * a row's hover (intent preload) and on the navigation itself. A pane whose module already arrived renders
 * synchronously (no Suspense fallback for a frame); a cold one suspends into the caller's skeleton.
 * Why the panes are not a route component: docs/notes/app/list-detail-squash.md.
 */
export function lazyPanes<M>(load: () => Promise<M>) {
  let loaded: M | undefined
  let loading: Promise<M> | undefined

  const preload = (): Promise<M> =>
    (loading ??= load().then(
      (module) => (loaded = module),
      (error: unknown) => {
        // A failed chunk fetch must be retryable by the next hover or render, not cached forever.
        loading = undefined
        throw error
      },
    ))

  const pane = <P extends object>(pick: (module: M) => ComponentType<P>) =>
    lazy(() =>
      loaded ? resolvedNow({ default: pick(loaded) }) : preload().then((module) => ({ default: pick(module) })),
    )

  return { preload, pane }
}

/** A thenable that settles inside `then` itself, which React.lazy reads as already resolved. */
function resolvedNow<T>(value: T): Promise<T> {
  // oxlint-disable-next-line unicorn/no-thenable -- React.lazy only calls `then`, and nothing awaits this
  return { then: (onFulfilled: (value: T) => unknown) => onFulfilled(value) } as unknown as Promise<T>
}

/**
 * Loads the panes of every route the document's URL matches, for the client's hydrate hook to await. A pane still
 * loading when its dehydrated boundary re-renders is thrown away and client-rendered: its skeleton flashes over the
 * server's markup, and on a phone that lands mid back-swipe (nav-stack e2e). A failed fetch does not block hydration.
 */
export function preloadMatchedPanes(router: AnyRouter, pathname: string): Promise<unknown> {
  const [routes] = router.getMatchedRoutes(pathname)
  const loads: Promise<unknown>[] = []
  for (const route of routes) {
    const panes = route.options.staticData?.detailPanes
    if (panes) loads.push(panes.preload().catch(() => undefined))
  }
  return Promise.all(loads)
}
